import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

/**
 * Bid Summary Card - displays calculated totals
 * Uses job.estimate_total for the effective grand total (includes change orders)
 */
export const BidSummaryCard = ({ 
  calculations, 
  bidSheet,
  formatWholeDollars,
  formatCurrency,
  title = "Bid Summary",
  changeOrders = [],  // Array of change orders from the job
  effectiveTotal = null,  // job.estimate_total from backend (already includes COs)
}) => {
  // Filter approved change orders (has date_approved set)
  const approvedCOs = changeOrders.filter(co => co.date_approved);
  const hasApprovedCOs = approvedCOs.length > 0;
  
  // Use backend's estimate_total if provided, otherwise calculate
  const grandTotalWithCOs = effectiveTotal !== null 
    ? effectiveTotal 
    : calculations.grandTotal + approvedCOs.reduce((sum, co) => sum + (co.amount || 0), 0);

  return (
    <Card className="border-2 border-primary/20">
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-2">
          <div className="flex justify-between">
            <span>Materials Subtotal</span>
            <span className="font-mono">{formatWholeDollars(calculations.materialSubtotal)}</span>
          </div>
          <div className="flex justify-between">
            <span>Labor Subtotal</span>
            <span className="font-mono">{formatWholeDollars(calculations.laborSubtotal)}</span>
          </div>
          <div className="flex justify-between">
            <span>Equipment Subtotal</span>
            <span className="font-mono">{formatWholeDollars(calculations.equipmentSubtotal)}</span>
          </div>
          {calculations.miscSubtotal > 0 && (
            <div className="flex justify-between">
              <span>Misc Subtotal</span>
              <span className="font-mono">{formatWholeDollars(calculations.miscSubtotal)}</span>
            </div>
          )}
          <hr className="my-2" />
          <div className="flex justify-between text-muted-foreground">
            <span>Burden ({bidSheet.burden_percent}% on Labor)</span>
            <span className="font-mono">{formatWholeDollars(calculations.burdenAmount)}</span>
          </div>
          <hr className="my-2" />
          <div className="flex justify-between font-medium">
            <span>Subtotal Before Profit</span>
            <span className="font-mono">{formatWholeDollars(calculations.subtotalBeforeProfit)}</span>
          </div>
          <div className="flex justify-between text-muted-foreground">
            <span>Profit ({bidSheet.profit_percent}%)</span>
            <span className="font-mono">{formatWholeDollars(calculations.profitAmount)}</span>
          </div>
          <hr className="my-2" />
          {!bidSheet.tax_exempt && (
            <div className="flex justify-between text-muted-foreground">
              <span>Sales Tax ({bidSheet.sales_tax_percent}% on Taxable Materials)</span>
              <span className="font-mono">{formatWholeDollars(calculations.taxAmount)}</span>
            </div>
          )}
          {bidSheet.tax_exempt && (
            <div className="flex justify-between text-green-600">
              <span>Tax Exempt</span>
              <span>$0</span>
            </div>
          )}
          <hr className="my-2" />
          
          {/* If there are approved change orders, show "Total" first, then COs, then "Grand Total" */}
          {hasApprovedCOs ? (
            <>
              <div className="flex justify-between text-lg font-semibold">
                <span>Total</span>
                <span className="font-mono">{formatWholeDollars(calculations.grandTotal)}</span>
              </div>
              
              {/* List approved change orders */}
              <div className="mt-2 space-y-1 pl-4 border-l-2 border-primary/30">
                {approvedCOs.map((co, idx) => (
                  <div key={co.id || idx} className={`flex justify-between text-sm ${co.amount < 0 ? 'text-red-600' : 'text-green-600'}`}>
                    <span>CO #{co.co_number || idx + 1}: {co.description || 'Change Order'}</span>
                    <span className="font-mono">
                      {co.amount >= 0 ? '+' : ''}{formatWholeDollars(co.amount || 0)}
                    </span>
                  </div>
                ))}
              </div>
              
              <hr className="my-2" />
              <div className="flex justify-between items-center text-2xl font-bold bg-slate-900 text-white rounded-lg px-4 py-3 shadow-sm" data-testid="bid-grand-total">
                <span>Grand Total</span>
                <span className="font-mono">{formatWholeDollars(grandTotalWithCOs)}</span>
              </div>
            </>
          ) : (
            <div className="flex justify-between items-center text-2xl font-bold bg-slate-900 text-white rounded-lg px-4 py-3 shadow-sm" data-testid="bid-grand-total">
              <span>Grand Total</span>
              <span className="font-mono">{formatWholeDollars(calculations.grandTotal)}</span>
            </div>
          )}
          
          {bidSheet.scaffold_sqft > 0 && (
            <div className="flex justify-between text-lg font-semibold text-blue-600 mt-2 p-3 bg-blue-50 rounded-lg">
              <span>Price per Sq. Ft. ({bidSheet.scaffold_sqft.toLocaleString()} sq ft)</span>
              <span className="font-mono">{formatCurrency(calculations.pricePerSqft)}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default BidSummaryCard;
